import React, { useEffect, useMemo, useState } from 'react';
import { useActiveFilters, FilterEntry } from '../../../hooks/useActiveFilters';
import { FilterBadge, FilterChips } from '../../Shared/Filter/ActiveFilterDisplay';
import { ChevronDoubleRightIcon, ChevronDoubleLeftIcon, MagnifyingGlassIcon, FunnelIcon, ArrowPathIcon, PlusIcon, MinusIcon } from "@heroicons/react/20/solid";
import { Disclosure } from '@headlessui/react';
import { AssessmentQuestionnaire, AssessmentTemplate, Question, Skill } from '../../../Models';
import Slider from 'rc-slider';

import { fetchAllSkills } from '../../../api/skill.api';
import { fetchAssessmentTemplates, fetchAssessmentTemplatesWithQuestionnaires } from '../../../api/assessmenttemplate.api';
import useApiToken from '../../../hooks/useApiToken';
import { getAllAssessments } from '../../../api/assessmentquestionnaires.api';

interface Options {
    id: string;
    name: string;
}

interface FilterSection {
    name: string;
    options: Options[];
}

interface AssessmentLeftSideBarProps {
    onToggle: (isOpen: boolean) => void;
    handleAssessmentSearch: (event: React.ChangeEvent<HTMLInputElement> | string) => void;
    getQuestionFilters: Function,
    currentPage: number
}

const AssessmentLeftSideBar: React.FC<AssessmentLeftSideBarProps> = ({ onToggle, handleAssessmentSearch, getQuestionFilters, currentPage }) => {
    const token = useApiToken();
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [currentDifficulty, setCurrentDifficulty] = useState({ min: 0, max: 100 });
    const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({
        'Templates': ['all'],
        'Skills': ['all'],
    });
    const [prevAccuracy, setPrevAccuracy] = useState({ min: 0, max: 100 })
    const [skills, setSkils] = useState<Skill[]>([]);
    const [templates, setTemplates] = useState<AssessmentTemplate[]>([]);

    const filterEntries = useMemo<FilterEntry[]>(() => {
        const entries: FilterEntry[] = [];
        Object.entries(selectedFilters).forEach(([key, vals]) => {
            const safeVals = Array.isArray(vals) ? vals : [];
            if (!safeVals.includes('all')) {
                const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                
                const displayNames = safeVals.map(valId => {
                    if (key === 'Templates') {
                        return templates.find(t => t.id === valId)?.name || valId;
                    } else if (key === 'Skills') {
                        return skills.find(s => s.id === valId)?.name || valId;
                    }
                    return valId;
                });

                entries.push({ 
                    label, 
                    active: true, 
                    displayValue: displayNames.join(', '),
                    onRemove: () => {
                        setSelectedFilters(prev => ({ ...prev, [key]: ['all'] }));
                    }
                });
            }
        });
        if (currentDifficulty.min !== 0 || currentDifficulty.max !== 100)
            entries.push({ 
                label: 'Difficulty', 
                active: true, 
                displayValue: `${currentDifficulty.min}–${currentDifficulty.max}`,
                onRemove: () => {
                    setCurrentDifficulty({ min: 0, max: 100 });
                    setPrevAccuracy({ min: 0, max: 100 });
                }
            });
        return entries;
    }, [selectedFilters, currentDifficulty, templates, skills]);

    const { activeFilterCount, activeFilterNames } = useActiveFilters(filterEntries);


    useEffect(() => {
        if (!token) return;
        fetchAllSkills(token).then((res) => setSkils(res));
        fetchAssessmentTemplates().then(res => setTemplates(res));
    }, [token]);

    const handleToggle = () => {
        setIsSidebarOpen(!isSidebarOpen);
        onToggle(!isSidebarOpen);
    };

    const handleResetFilters = () => {
        handleAssessmentSearch(searchQuery);
        setSearchQuery('');
        setSelectedFilters({
            'Templates': ['all'],
            'Skills': ['all'],
        });
        setCurrentDifficulty({ min: 0, max: 100 });
        setPrevAccuracy({ min: 0, max: 100 });
    }


    const filterSections: FilterSection[] = [
        {
            name: 'Templates',
            options: [
                { id: 'all', name: 'All' },
                ...templates.map(template => ({ id: template.id, name: template.name }))
            ],
        },
        {
            name: 'Skills',
            options: [
                { id: 'all', name: 'All' },
                ...skills.map(skill => ({ id: skill.id, name: skill.name }))
            ],
        },
        {
            name: 'Difficulty',
            options: [],
        },


    ];
    useEffect(() => {
        getQuestionFilters(selectedFilters, currentDifficulty, searchQuery);
    }, [currentPage])

    const handleFilterChange = (sectionName: string, id: string) => {
        setSelectedFilters((prevFilters) => {
            let newSelected = [...prevFilters[sectionName]];
            if (id === 'all') {
                newSelected = ['all'];
            } else {
                newSelected = newSelected.filter(item => item !== 'all');
                if (newSelected.includes(id)) {
                    newSelected = newSelected.filter(item => item !== id);
                } else {
                    newSelected.push(id);
                }

                if (newSelected.length === 0) {
                    newSelected = ['all'];
                }
            }

            return {
                ...prevFilters,
                [sectionName]: newSelected,
            };
        });
    };

    useEffect(() => {
        getQuestionFilters(selectedFilters, currentDifficulty, searchQuery);
    }, [selectedFilters, currentDifficulty, searchQuery])
    return (
        <aside
            className={`select-none fixed z-10 top-[4.1rem] block h-[calc(99vh-4rem)] left-0 shadow-md border dark:border-gray-500 transition-transform transform duration-200 w-[19rem] ${isSidebarOpen ? "translate-x-0" : "-translate-x-[100%]"
                }`}
        >
            <div className="mx-auto bg-gray-100 dark:bg-darkbglight">
                <div className="min-w-0 px-4 pt-2 pb-1">
                    <div className="text-gray-900 dark:text-gray-300 flex items-center space-x-2 justify-center">
                        <h3 className="text-center text-lg font-semibold leading-7 sm:text-base sm:tracking-tight">
                            Assessment Filters
                        </h3>
                    </div>
                </div>
            </div>
            <div
                className={`absolute top-10 ${isSidebarOpen ? "-right-2 shadow-md" : "-right-4 shadow-xl"
                    } h-16 border border-gray-300 rounded-md flex items-center justify-center cursor-pointer bg-white dark:bg-darkbglight dark:border-gray-500 dark:text-gray-300`}
                onClick={handleToggle}
            >
                {isSidebarOpen ? (
                    <ChevronDoubleLeftIcon width={16} />
                ) : (
                    <ChevronDoubleRightIcon width={16} />
                )}
            </div>
            <div className="pb-12 h-full bg-white thin-scroll overflow-y-auto dark:bg-darkbg w-full flex flex-col items-center mt-2">
                <div className="relative flex focus-within:z-10 mt-2 items-center justify-between w-[90%] mb-4">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
                        <MagnifyingGlassIcon
                            className="h-5 w-5 text-gray-400 dark:text-gray-300"
                            aria-hidden="true"
                        />
                    </div>
                    <input
                        type="text"
                        name="search"
                        id="search"
                        className="block w-10/12 border-0 pl-9 text-gray-900 ring-1 ring-inset ring-gray-300 dark:ring-gray-500 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-entntorange sm:text-sm bg-transparent dark:text-gray-300 dark:placeholder:text-gray-400 rounded-md"
                        placeholder="Quick Search"
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            // handleAssessmentSearch(e);
                        }}
                        value={searchQuery}
                    />
                    <div
                        className="text-gray-400 hover:text-gray-500 cursor-pointer relative dark:hover:text-gray-300"
                        onClick={handleResetFilters}
                        title="Reset all filters"
                    >
                        <FunnelIcon width={24} />
                        <ArrowPathIcon width={14} className="absolute bottom-0 -right-1" />
                        <FilterBadge count={activeFilterCount} />
                    </div>
                </div>
                <FilterChips names={activeFilterNames} />
                {filterSections.map((section, index: number) => (
                    <Disclosure as="div" key={index} className="w-[90%] border-b border-gray-200 dark:border-gray-500">
                        {({ open }) => (
                            <>
                                <Disclosure.Button className="flex w-full justify-between py-3 text-sm text-gray-400 hover:text-gray-500 dark:text-gray-300 font-semibold">
                                    <span className='text-gray-900 dark:text-gray-400'>{section.name.split("_").join(" ")}</span>
                                    {open ? (
                                        <MinusIcon className="h-5 w-5 text-gray-200" />
                                    ) : (
                                        <PlusIcon className="h-5 w-5 text-gray-200" />
                                    )}
                                </Disclosure.Button>
                                <Disclosure.Panel className="pb-2 text-sm text-gray-500 dark:text-gray-400">
                                    {section.name === 'Difficulty' ? (
                                        <div key={index} className="flex items-center m-2 p-4">
                                            <Slider
                                                range
                                                max={100}
                                                min={0}
                                                value={[prevAccuracy.min, prevAccuracy.max]}
                                                onChange={(value: any) => {
                                                    const [min, max] = value as number[];
                                                    setPrevAccuracy({ min, max })
                                                }}
                                                onChangeComplete={(value: any) => {
                                                    const [min, max] = value as number[];
                                                    setCurrentDifficulty({ min, max });
                                                    setPrevAccuracy({ min, max })

                                                }}
                                                marks={{
                                                    0: 0,
                                                    [100]: 100,
                                                }}
                                                styles={{
                                                    track: { background: "#0D42EC" },
                                                    handle: { borderColor: "#0D42EC" },
                                                }}
                                            />
                                        </div>
                                    ) : (
                                        section.options.map((option) => (
                                            <div key={option.id} className="flex items-center mb-2">
                                                <input
                                                    id={`${section.name}-${option.id}`}
                                                    name={`${section.name}[]`}
                                                    type="checkbox"
                                                    checked={selectedFilters[section.name].includes(option.id)}
                                                    onChange={() => handleFilterChange(section.name, option.id)}
                                                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                />
                                                <label
                                                    htmlFor={`${section.name}-${option.id}`}
                                                    className="ml-3 text-sm text-gray-600 dark:text-gray-400"
                                                >
                                                    {option.name}
                                                </label>
                                            </div>
                                        ))
                                    )}
                                </Disclosure.Panel>
                            </>
                        )}
                    </Disclosure>
                ))}

            </div>
        </aside>
    );
};

export default AssessmentLeftSideBar;
